import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { LoginArea } from '@/components/auth/LoginArea';
import { WalletPanel } from '@/components/WalletPanel';
import { SendDialog } from '@/components/SendDialog';
import { Card, CardContent } from '@/components/ui/card';
import { Bitcoin, Wallet } from 'lucide-react';

// ── Main page ─────────────────────────────────────────────────────────────────

const Index = () => {
  useSeoMeta({
    title: 'BURN THE DOX SATS',
    description:
      'Send Bitcoin directly to any Nostr identity. Every npub is a Taproot address. Help make bitcoin even more scarce.',
  });

  const { user } = useCurrentUser();
  const [sendOpen, setSendOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-white dark:from-zinc-900 dark:via-zinc-950 dark:to-zinc-950">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center shadow-sm">
              <Bitcoin className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight">
              BURN THE <span className="text-orange-500">DOX SATS</span>
            </span>
          </div>
          <LoginArea className="max-w-48" />
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-2xl mx-auto px-4 pt-12 pb-8 text-center">
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-3">
          BURN THE <span className="text-orange-500">DOX SATS</span>
        </h1>
        <p className="text-base text-muted-foreground mt-2">
          And help make bitcoin even more scarce.
        </p>
      </section>

      {/* Main content */}
      <main className="max-w-2xl mx-auto px-4 pb-20">
        {user ? (
          <>
            <WalletPanel onSend={() => setSendOpen(true)} />
            <SendDialog
              open={sendOpen}
              onOpenChange={setSendOpen}
              defaultRecipient="1BurnchainBurnchainBurnchain6cU7ZK"
            />
          </>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-12 px-6 text-center space-y-4">
              <Wallet className="w-10 h-10 mx-auto text-orange-300" />
              <div>
                <p className="font-semibold">Your Bitcoin wallet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Log in with your Nostr key to access your Taproot wallet, check your balance,
                  and send sats.
                </p>
              </div>
              <LoginArea className="max-w-48 mx-auto" />
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t py-6 text-center text-xs text-muted-foreground">
        <a
          href="https://shakespeare.diy"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-foreground transition-colors"
        >
          Vibed with Shakespeare
        </a>
        <span className="mx-2">·</span>
        <a
          href="https://mempool.space"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-foreground transition-colors"
        >
          Powered by mempool.space
        </a>
      </footer>
    </div>
  );
};

export default Index;
